<template>
  <div>
    <v-overlay v-if="generatingInvoice" opacity="0.1">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <!--generate filters-->
    <v-row>
      <!--organization-->
      <v-col cols="12" sm="6">
        <v-autocomplete
          v-model="selectedOrganization"
          :items="organizations"
          item-value="_id"
          :disabled="disabled"
          hide-details
          flat
          solo
          :label="$t('invoice.organization')"
          class="rounded-lg"
          clearable
          :filter="customLocalisedAutoCompleteFilter"
          :loading="loadingOrganizations"
        >
          <template v-slot:selection="{ item }">
            {{ getLocalisedValue(item.name) }}
          </template>

          <template v-slot:item="{ item }">
            {{ getLocalisedValue(item.name) }}
          </template>
        </v-autocomplete>
      </v-col>

      <!--date-->
      <v-col cols="12" sm="6">
        <v-menu
          v-model="showDateSelector"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          :disabled="disabled || !selectedOrganization"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedDate"
              v-bind="attrs"
              v-on="on"
              hide-details
              :label="$t('invoice.date')"
              class="rounded-lg"
              flat
              :disabled="disabled || !selectedOrganization"
              solo
              readonly
              append-icon="mdi-calendar-blank-outline"
              clearable
            >
            </v-text-field>
          </template>

          <v-date-picker
            v-if="availableDates.length"
            v-model="selectedDate"
            no-title
            color="primary"
            :disabled="disabled || !selectedOrganization"
            :allowed-dates="val => availableDates.includes(val)"
            :min="minDate"
            :max="maxDate"
            :show-current="maxDate"
            @input="showDateSelector = false"
            style="width: 100%"
          />

          <v-list v-else>
            <v-list-item>
              <v-list-item-title> No data available </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <!--generate filters-->
    <v-row>
      <!--category-->
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          :disabled="disabled || !selectedOrganization || !selectedDate"
          hide-details
          v-model="selectedTrainingCategory"
          :items="trainingCategories"
          item-value="_id"
          flat
          solo
          :label="$t('invoice.category')"
          class="rounded-lg"
          clearable
          :filter="customLocalisedAutoCompleteFilter"
          :loading="loadingTrainingCategories"
        >
          <template v-slot:selection="{ item }">
            {{ getLocalisedValue(item.name) }}
          </template>

          <template v-slot:item="{ item }">
            {{ getLocalisedValue(item.name) }}
          </template>
        </v-autocomplete>
      </v-col>

      <!--sub category-->
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          :disabled="
            disabled ||
            !selectedOrganization ||
            !selectedDate ||
            !selectedTrainingCategory
          "
          hide-details
          v-model="selectedTrainingSubCategory"
          :items="trainingSubCategories"
          item-value="_id"
          flat
          solo
          :label="$t('invoice.subCategory')"
          class="rounded-lg"
          clearable
          :filter="customLocalisedAutoCompleteFilter"
        >
          <template v-slot:selection="{ item }">
            {{ getLocalisedValue(item.name) }}
          </template>

          <template v-slot:item="{ item }">
            {{ getLocalisedValue(item.name) }}
          </template>
        </v-autocomplete>
      </v-col>

      <!--courses-->
      <v-col cols="12" sm>
        <v-autocomplete
          v-model="selectedTraining"
          :items="trainings"
          item-value="_id"
          flat
          solo
          :disabled="disabled || !selectedOrganization || !selectedDate"
          hide-details
          :label="$t('invoice.course')"
          class="rounded-lg"
          clearable
          :filter="customLocalisedAutoCompleteFilter"
        >
          <template v-slot:selection="{ item }">
            {{ getLocalisedValue(item.title) }}
          </template>

          <template v-slot:item="{ item }">
            {{ getLocalisedValue(item.title) }}
          </template>
        </v-autocomplete>
      </v-col>

      <!--add-->
      <v-col cols="12" sm="auto">
        <v-btn
          depressed
          block
          style="height: 47px"
          color="button"
          :disabled="
            disabled ||
            !selectedOrganization ||
            !selectedDate ||
            !selectedTraining
          "
          class="rounded-lg"
          @click="showTrainingDetailsDialog = true"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!--selected items table heading-->
    <v-row class="mt-10">
      <v-col cols="12" class="text-start">
        <span class="text-h5 font-weight-medium textLight--text">
          {{ $t("invoice.courses") }}
        </span>
      </v-col>
    </v-row>

    <!--selected items table-->
    <v-row>
      <v-col>
        <InvoiceOrderItemsTable :orderItems.sync="orderItems" />
      </v-col>
    </v-row>

    <!--other invoice data-->
    <v-row class="mt-16">
      <v-col>
        <v-card
          flat
          class="
            rounded-lg
            pa-7
            text-subtitle-1
            font-weight-light
            textLight--text
          "
        >
          <ValidationObserver ref="otherInvoiceDataObserver">
            <v-row>
              <!--left section-->
              <v-col cols="12" md>
                <!--invoice amount-->
                <v-row align="center">
                  <v-col cols="12" sm> {{ $t("invoice.subTotal") }}</v-col>

                  <v-col cols="12" sm>
                    <validation-provider v-slot="{ errors }">
                      <v-text-field
                        :value="`${currency} ${formatPrice(
                          calculateInvoiceAmount(orderItems)
                        )}`"
                        :error-messages="errors"
                        dense
                        hide-details
                        flat
                        solo
                        readonly
                      >
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-divider></v-divider>
                  </v-col>
                </v-row>

                <!--invoice date-->
                <v-row align="center">
                  <v-col cols="12" sm> {{ $t("invoice.invoiceDate") }}</v-col>

                  <v-col cols="12" sm>
                    <v-menu
                      v-model="showInvoiceDateSelector"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                        >
                          <v-text-field
                            v-model="otherData.invoiceDate"
                            :error-messages="errors"
                            v-bind="attrs"
                            v-on="on"
                            dense
                            hide-details
                            flat
                            solo
                            clearable
                            prepend-icon="mdi-calendar-blank-outline"
                            placeholder="YYYY-MM-DD"
                            readonly
                            class="ps-2"
                          >
                          </v-text-field>
                        </validation-provider>
                      </template>

                      <v-date-picker
                        color="primary"
                        v-model="otherData.invoiceDate"
                        no-title
                        @input="showInvoiceDateSelector = false"
                        style="width: 100%"
                      />
                    </v-menu>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-divider></v-divider>
                  </v-col>
                </v-row>

                <!--delivery note-->
                <v-row align="center">
                  <v-col cols="12" sm> {{ $t("invoice.deliveryNote") }}</v-col>

                  <v-col cols="12" sm>
                    <validation-provider v-slot="{ errors }">
                      <v-textarea
                        v-model="otherData.deliveryNote"
                        :error-messages="errors"
                        dense
                        hide-details
                        flat
                        solo
                        clearable
                        :prefix="errors.length ? '*' : ''"
                        :placeholder="$t('invoice.deliveryNote')"
                        rows="4"
                      ></v-textarea>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" md="1" class="text-center">
                <v-divider :vertical="$vuetify.breakpoint.mdAndUp"></v-divider>
              </v-col>

              <!--right section-->
              <v-col cols="12" md>
                <!--mode of payment-->
                <v-row align="center">
                  <v-col cols="12" sm> {{ $t("invoice.modeOfPayment") }}</v-col>

                  <v-col cols="12" sm>
                    <validation-provider v-slot="{ errors }">
                      <v-text-field
                        v-model="otherData.paymentMode"
                        :error-messages="errors"
                        dense
                        hide-details
                        flat
                        solo
                        clearable
                        :prefix="errors.length ? '*' : ''"
                        :placeholder="$t('invoice.modeOfPayment')"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-divider></v-divider>
                  </v-col>
                </v-row>

                <!--buyer order no-->
                <v-row align="center">
                  <v-col cols="12" sm> {{ $t("invoice.buyerOrderNo") }}</v-col>

                  <v-col cols="12" sm>
                    <validation-provider v-slot="{ errors }" rules="required">
                      <v-text-field
                        v-model="otherData.buyerOrderNo"
                        :error-messages="errors"
                        dense
                        hide-details
                        flat
                        solo
                        clearable
                        :prefix="errors.length ? '*' : ''"
                        :placeholder="$t('invoice.buyerOrderNo')"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-divider></v-divider>
                  </v-col>
                </v-row>

                <!--buyer order date-->
                <v-row align="center">
                  <v-col cols="12" sm>
                    {{ $t("invoice.buyerOrderDate") }}
                  </v-col>

                  <v-col cols="12" sm>
                    <v-menu
                      v-model="showBuyerOrderDateSelector"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                        >
                          <v-text-field
                            v-model="otherData.buyerOrderDate"
                            :error-messages="errors"
                            v-bind="attrs"
                            v-on="on"
                            dense
                            hide-details
                            flat
                            solo
                            clearable
                            prepend-icon="mdi-calendar-blank-outline"
                            placeholder="YYYY-MM-DD"
                            readonly
                            class="ps-2"
                          >
                          </v-text-field>
                        </validation-provider>
                      </template>

                      <v-date-picker
                        color="primary"
                        v-model="otherData.buyerOrderDate"
                        no-title
                        @input="showBuyerOrderDateSelector = false"
                        style="width: 100%"
                      />
                    </v-menu>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col>
                    <v-divider></v-divider>
                  </v-col>
                </v-row>

                <!--terms of delivery-->
                <v-row align="center">
                  <v-col cols="12" sm>
                    {{ $t("invoice.termsOfDelivery") }}
                  </v-col>

                  <v-col cols="12" sm>
                    <validation-provider v-slot="{ errors }">
                      <v-text-field
                        v-model="otherData.termsOfDelivery"
                        :error-messages="errors"
                        dense
                        hide-details
                        flat
                        solo
                        clearable
                        :prefix="errors.length ? '*' : ''"
                        :placeholder="$t('invoice.termsOfDelivery')"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </ValidationObserver>
        </v-card>
      </v-col>
    </v-row>

    <!--generate button-->
    <v-row justify="end" class="mt-5">
      <v-col cols="auto">
        <v-btn
          depressed
          color="primary"
          :loading="generatingInvoice"
          :disabled="!orderItems.length"
          class="rounded-1 subtitle-2 text-capitalize"
          @click="generateTrainingRegistrationInvoice"
        >
          {{ $t("invoice.generate") }}
        </v-btn>
      </v-col>
    </v-row>

    <!--course selection dialog-->
    <InvoiceOrderItemDialog
      v-if="selectedTrainingDetails"
      :showDialogProp.sync="showTrainingDetailsDialog"
      :orderItemProp="selectedTrainingDetails"
      @onSubmit="addToOrderItems"
    />

    <!--snackbar success-->
    <v-snackbar
      :value="snackbarSuccess"
      @input="resetSnackbarSuccess"
      timeout="3000"
      color="success"
    >
      {{ snackbarSuccessMessage || $t("invoice.generateSuccess") }}

      <template v-slot:action="{ attrs }">
        <v-btn
          v-bind="attrs"
          color="primary"
          text
          @click="resetSnackbarSuccess"
        >
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>

    <!--snackbar error-->
    <v-snackbar
      :value="snackbarError"
      @input="resetSnackbarError"
      timeout="3000"
      color="error"
    >
      {{ snackbarErrorMessage || $t("somethingWentWrong") }}

      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" color="primary" text @click="resetSnackbarError">
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import InvoiceOrderItemsTable from "@/app/invoices-module/components/InvoiceOrderItemsTable";
import InvoiceOrderItemDialog from "@/app/invoices-module/components/InvoiceOrderItemDialog";

import "../../core-module/util/validationRules";
import { generateTrainingRegistrationInvoice } from "@/app/invoices-module/methods/generateTrainingRegistrationInvoice";

export default {
  name: "InvoiceTrainingRegistrationSelector",
  components: { InvoiceOrderItemsTable, InvoiceOrderItemDialog },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loadingOrganizations: true,
      loadingTrainingCategories: true,
      loadingTrainingRegistrations: true,
      generatingInvoice: false,
      snackbarSuccess: false,
      snackbarError: false,
      snackbarSuccessMessage: null,
      snackbarErrorMessage: null,

      organizations: [],
      selectedOrganization: null,

      trainingRegistrations: [],

      showDateSelector: false,
      selectedDate: null,

      trainingCategories: [],
      selectedTrainingCategory: null,
      selectedTrainingSubCategory: null,

      selectedTraining: null,

      showTrainingDetailsDialog: false,
      selectedTrainingDetails: null,

      orderItems: [],
      otherData: {},
      showInvoiceDateSelector: false,
      showBuyerOrderDateSelector: false,
    };
  },
  computed: {
    currency() {
      return this.$store.getters.currency;
    },
    availableDates() {
      return (this.trainingRegistrations ?? []).reduce((result, x) => {
        if (
          !this.isExistingTrainingRegistrationInOrderItems(x._id) &&
          x.startDate != null
        ) {
          const startDate = this.moment(parseInt(x.startDate)).format(
            "YYYY-MM-DD"
          );

          if (!result.includes(startDate)) {
            result.push(startDate);
          }
        }

        return result;
      }, []);
    },
    minDate() {
      return this.availableDates?.length ? this.availableDates[0] : null;
    },
    maxDate() {
      return this.availableDates?.length
        ? this.availableDates[this.availableDates.length - 1]
        : null;
    },
    trainingSubCategories() {
      if (this.selectedTrainingCategory != null) {
        return (
          this.trainingCategories?.find(
            x => x._id === this.selectedTrainingCategory
          )?.subCategories ?? []
        );
      }

      return [];
    },
    trainings() {
      return (this.trainingRegistrations ?? []).reduce((result, x) => {
        if (
          !this.isExistingTrainingRegistrationInOrderItems(x._id) &&
          x.startDate != null &&
          this.selectedDate != null
        ) {
          const startDate = this.moment(parseInt(x.startDate)).format(
            "YYYY-MM-DD"
          );

          if (startDate === this.selectedDate) {
            let canPush = true;

            if (this.selectedTrainingCategory) {
              canPush =
                x.training?.trainingCategories?.some(
                  tc => tc._id === this.selectedTrainingCategory
                ) ?? false;
            }

            if (canPush && this.selectedTrainingSubCategory) {
              canPush =
                x.training?.trainingSubCategories?.some(
                  tc => tc._id === this.selectedTrainingSubCategory
                ) ?? false;
            }

            if (canPush) result.push(x.training);
          }
        }

        return result;
      }, []);
    },
  },
  apollo: {
    getOrganizations() {
      return {
        query: require("../../organization-module/api/query/getOrganizations.gql"),
        variables: { limit: 0, skip: 0, filterInput: null },
        result({ data, loading }) {
          if (data?.getOrganizations?.organizations) {
            this.organizations = data.getOrganizations.organizations;
          }

          this.loadingOrganizations = loading;
        },
      };
    },
    getTrainingCategories() {
      return {
        query: require("../../category-module/api/query/getTrainingCategories.gql"),
        variables: { pageInput: { skip: 0, limit: 0 } },
        result({ data, loading }) {
          if (data?.getTrainingCategories?.trainingCategories) {
            this.trainingCategories =
              data.getTrainingCategories.trainingCategories;
          }

          this.loadingTrainingCategories = loading;
        },
      };
    },
    getTrainingRegistrationsForInvoiceGeneration() {
      return {
        query: require("../api/query/getTrainingRegistrationsForInvoiceGeneration.gql"),
        variables: {
          limit: 0,
          skip: 0,
          filterInput: { organization: this.selectedOrganization },
        },
        skip: () => true,
        result({ data, loading }) {
          if (
            data?.getTrainingRegistrationsForInvoiceGeneration
              ?.trainingRegistrations
          ) {
            this.trainingRegistrations =
              data.getTrainingRegistrationsForInvoiceGeneration.trainingRegistrations
                .filter(x => !x.invoice?._id)
                .sort((a, b) => a.startDate - b.startDate);
          }

          this.loadingTrainingRegistrations = loading;
        },
      };
    },
  },
  watch: {
    selectedOrganization() {
      this.trainingRegistrations = [];

      this.showDateSelector = false;
      this.selectedDate = null;

      this.selectedTrainingCategory = null;
      this.selectedTrainingSubCategory = null;

      this.selectedTraining = null;

      this.showTrainingDetailsDialog = false;
      this.selectedTrainingDetails = null;

      this.orderItems = [];
      this.otherData = {};

      this.fetchTrainingRegistrations();
    },
    selectedDate() {
      this.selectedTraining = null;

      this.showTrainingDetailsDialog = false;
      this.selectedTrainingDetails = null;
    },
    selectedTrainingCategory() {
      this.selectedTrainingSubCategory = null;

      this.selectedTraining = null;

      this.showTrainingDetailsDialog = false;
      this.selectedTrainingDetails = null;
    },
    selectedTrainingSubCategory() {
      this.selectedTraining = null;

      this.showTrainingDetailsDialog = false;
      this.selectedTrainingDetails = null;
    },
    showTrainingDetailsDialog() {
      if (this.showTrainingDetailsDialog && this.selectedTraining) {
        const trainingData = this.trainings.find(
          x => x._id === this.selectedTraining
        );

        if (trainingData?._id) {
          const filteredTrainingRegistrations = (
            this.trainingRegistrations ?? []
          ).reduce((result, x) => {
            if (
              !this.isExistingTrainingRegistrationInOrderItems(x._id) &&
              x.startDate != null &&
              this.selectedDate != null
            ) {
              const startDate = this.moment(parseInt(x.startDate)).format(
                "YYYY-MM-DD"
              );

              if (
                // TODO:DOUBT: should filter respect to selected date(uncomment below line) or add all(comment below line)?
                startDate === this.selectedDate &&
                x.training._id === trainingData._id
              ) {
                result.push(x);
              }
            }

            return result;
          }, []);

          const registeredCount = filteredTrainingRegistrations.filter(
            x => x.status === "REGISTERED"
          ).length;

          const startedCount = filteredTrainingRegistrations.filter(
            x => x.status === "THIRDPARTY"
          ).length;

          const completedCount = filteredTrainingRegistrations.filter(
            x => x.status === "COMPLETED"
          ).length;

          this.selectedTrainingDetails = {
            filteredTrainingRegistrations,
            training: this.selectedTraining,
            // trainingRegistrationDetailsList: filteredTrainingRegistrations.map(
            //   x => ({ trainingRegistration: x._id, employee: x.employee._id })
            // ),
            registeredCount,
            startedCount,
            completedCount,
            isRegisteredCountSelected: registeredCount > 0,
            isStartedCountSelected: startedCount > 0,
            isCompletedCountSelected: completedCount > 0,
            trainingTitle: trainingData.title,
            trainingPrice: trainingData.price ?? 0,
            unitPrice: trainingData.price ?? 0,
            discount: 0,
            quantity: filteredTrainingRegistrations.length,
          };
        }
      } else {
        this.selectedTrainingDetails = null;
      }
    },
  },
  methods: {
    resetSnackbarSuccess() {
      this.snackbarSuccess = false;
      this.snackbarSuccessMessage = null;
    },
    resetSnackbarError() {
      this.snackbarError = false;
      this.snackbarErrorMessage = null;
    },
    isExistingTrainingRegistrationInOrderItems(trainingRegistrationId) {
      return (
        trainingRegistrationId &&
        (this.orderItems?.some(
          oi =>
            oi?.trainingRegistrationDetailsList?.some(
              oitl => oitl?.trainingRegistration === trainingRegistrationId
            ) ?? false
        ) ??
          false)
      );
    },
    fetchTrainingRegistrations() {
      if (!this.selectedOrganization) return;

      this.$apollo.queries.getTrainingRegistrationsForInvoiceGeneration.refetch(
        {
          filterInput: { organization: this.selectedOrganization },
        }
      );

      this.$apollo.queries.getTrainingRegistrationsForInvoiceGeneration.skip = false;
    },
    addToOrderItems(orderItem) {
      const existingOrderItemIndex =
        this.orderItems?.findIndex(x => x.training === orderItem.training) ??
        -1;

      // TODO:DOUBT: if respect to selected date filter enabled ask merge or not?
      const mergeOrderItem = false;

      if (mergeOrderItem && existingOrderItemIndex >= 0) {
        const existingOrderItem = this.orderItems[existingOrderItemIndex];
        const updatedQuantity = existingOrderItem.quantity + orderItem.quantity;

        this.$set(this.orderItems, existingOrderItemIndex, {
          ...existingOrderItem,
          trainingRegistrationDetailsList: [
            ...existingOrderItem.trainingRegistrationDetailsList,
            ...orderItem.trainingRegistrationDetailsList,
          ],
          unitPrice: orderItem.unitPrice,
          discount: orderItem.discount,
          quantity: updatedQuantity,
          amount: updatedQuantity * (orderItem.unitPrice - orderItem.discount),
        });
      } else {
        this.orderItems.push(orderItem);
      }

      this.selectedDate = null;

      this.selectedTraining = null;

      this.showTrainingDetailsDialog = false;
      this.selectedTrainingDetails = null;
    },
    calculateInvoiceAmount(orderItems) {
      return orderItems.reduce((result, x) => {
        return result + x.amount;
      }, 0);
    },
    calculateInvoiceQuantity(orderItems) {
      return orderItems.reduce((result, x) => {
        return result + x.quantity;
      }, 0);
    },
    async generateTrainingRegistrationInvoice() {
      const isValid = await this.$refs.otherInvoiceDataObserver.validate();
      if (!isValid) return;

      if (this.generatingInvoice) return;
      this.generatingInvoice = true;

      const organizationData = this.organizations.find(
        x => x._id === this.selectedOrganization
      );

      if (organizationData) {
        const input = {
          organizationDetails: {
            organization: organizationData._id,
            organizationName: organizationData.name,
            email: organizationData.email,
            phone: organizationData.phone,
            contactName: organizationData.contactName,
          },
          orderItems: this.orderItems.map(x => ({
            training: x.training,
            trainingRegistrationDetailsList: x.trainingRegistrationDetailsList,
            registeredCount: x.registeredCount,
            startedCount: x.startedCount,
            completedCount: x.completedCount,
            isRegisteredCountSelected: x.isRegisteredCountSelected,
            isStartedCountSelected: x.isStartedCountSelected,
            isCompletedCountSelected: x.isCompletedCountSelected,
            trainingTitle: x.trainingTitle,
            trainingPrice: x.trainingPrice,
            unitPrice: x.unitPrice,
            discount: x.discount,
            quantity: x.quantity,
          })),
          // invoiceNo: this.otherData.invoiceNo,
          invoiceDate: this.otherData.invoiceDate,
          buyerOrderNo: this.otherData.buyerOrderNo,
          buyerOrderDate: this.otherData.buyerOrderDate,
          deliveryNote: this.otherData.deliveryNote,
          paymentMode: this.otherData.paymentMode,
          termsOfDelivery: this.otherData.termsOfDelivery,
          // currency: this.otherData.currency,
          invoiceAmount: this.calculateInvoiceAmount(this.orderItems),
          invoiceQuantity: this.calculateInvoiceQuantity(this.orderItems),
          invoiceReference: this.otherData.invoiceReference,
          // bankDetails: this.otherData.bankDetails,
          // remarks: this.otherData.remarks,
          // status: this.otherData.status,
        };

        generateTrainingRegistrationInvoice(input).then(response => {
          if (response.data) {
            this.snackbarSuccess = true;

            this.selectedOrganization = null;

            // this.trainingRegistrations = [];
            //
            // this.showDateSelector = false;
            // this.selectedDate = null;
            //
            // this.selectedTrainingCategory = null;
            // this.selectedTrainingSubCategory = null;
            //
            // this.selectedTraining = null;
            //
            // this.showTrainingDetailsDialog = false;
            // this.selectedTrainingDetails = null;
            //
            // this.orderItems = [];
            // this.otherData = {};

            // location.reload();
            this.$emit("onGenerate");
          } else if (response.error) {
            this.snackbarError = true;
          }

          this.generatingInvoice = false;
        });
      } else {
        this.generatingInvoice = false;
      }
    },
  },
};
</script>
